import React, { Suspense, lazy, useEffect, useState } from "react";

import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { gql } from "@apollo/client";
import { useUser } from "@components/user-context";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import { useInOnboarding } from "@hooks/use-in-onboarding";
import { logEvent } from "@internal/analytics";
import { type CurrentStep as CurrentStepQuery } from "@north-beam/nb-client-side";
import { Steps, type Step, type StepName } from "@pages/onboarding/steps";

const OnboardingPage = lazy(() => import("@pages/onboarding"));

interface OnboardingContentProps {
  children: JSX.Element | JSX.Element[];
}

export function PreActivationContent({ children }: OnboardingContentProps) {
  const { user } = useUser();
  const [currentStep, setCurrentStep] = useState<StepName | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { hasFeatureFlag } = useFeatureFlags();
  const { addToast } = useToasts();
  const shouldShowOnboardingToast = hasFeatureFlag("onboardingToast");

  const { inOnboarding } = useInOnboarding();

  useEffect(() => {
    if (shouldShowOnboardingToast) {
      addToast(
        <div>
          <b>Onboarding is still ongoing</b>
          <p>
            Your account has been activated, but your data is still in
            development. Your numbers and data will not be correct until data
            validation is complete. <br></br>
            Please reach out to our onboarding team for any updates.
          </p>
          <p></p>
        </div>,
        {
          appearance: "warning",
          autoDismiss: false,
          blockDismiss: true,
        },
      );
    }
  }, [shouldShowOnboardingToast, addToast]);

  const { loading, data } =
    useNorthbeamQuery<CurrentStepQuery>(CURRENT_STEP_QUERY);

  useEffect(() => {
    const onboardingDetails = data?.me?.onboardingDetails;

    if (window.location.hash.length > 0) {
      let step: Step;
      if (onboardingDetails?.currentStep) {
        step =
          Steps.getModelSafe(onboardingDetails.currentStep, {
            strict: false,
            onError: () => {
              logEvent(
                "During onboarding, the value received for the latest step via the API is " +
                  "not valid.",
                { value: onboardingDetails.currentStep },
              );
            },
          }) || Steps.getModel(Steps.INTRODUCTION);
      } else {
        step = Steps.getModel(Steps.INTRODUCTION);
      }

      setCurrentStep(step.value);

      if (Array.from(searchParams).length > 0) {
        setSearchParams(searchParams);
      } else {
        setSearchParams({ step: step.value });
      }
      return;
    }

    if (onboardingDetails && !currentStep && inOnboarding) {
      let step: Step;
      if (onboardingDetails.currentStep) {
        step =
          Steps.getModelSafe(onboardingDetails.currentStep, {
            strict: false,
            onError: () => {
              logEvent(
                "During onboarding, the value received for the latest step via the API is " +
                  "not valid.",
                { value: onboardingDetails.currentStep },
              );
            },
          }) || Steps.getModel(Steps.INTRODUCTION);
      } else {
        step = Steps.getModel(Steps.INTRODUCTION);
      }

      setCurrentStep(step.value);

      if (
        !window.location.pathname ||
        !window.location.pathname.includes("onboarding")
      ) {
        window.location.pathname = window.location.pathname.split("/")[1];
      }
      window.location.hash = step.value;
    }
  }, [data, currentStep, inOnboarding, searchParams, setSearchParams]);

  // If the displayUser is activated, show the normal content.
  if (!inOnboarding) {
    return <>{children}</>;
  }

  /* If the displayUser
       a. has a defaultDisplayUser,
       b. isn't activated,
     Then the logged in user probably wants to see their `defaultDisplayUser`.
     We'll redirect, but only if the defaultDisplayUser is not the displayUser. */
  if (
    user.defaultDisplayUserId &&
    user.defaultDisplayUserId !== user.displayUserId
  ) {
    /* HACK HACK HACK
       It is possible for this to cause an infinite redirect by "chaining" default display user IDs
       Remove this code if the below error code is uncommon. */
    return <Navigate to={`/${user.defaultDisplayUserId}`} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="onboarding" />} />
      <Route
        path="/onboarding"
        element={
          <Suspense fallback={<></>}>
            {loading ? <></> : <OnboardingPage />}
          </Suspense>
        }
      />
    </Routes>
  );
}

const CURRENT_STEP_QUERY = gql`
  query CurrentStep {
    me {
      onboardingDetails {
        currentStep
        latestStep
      }
    }
  }
`;
